/**
 * PersonaContext — dual-axis persona state.
 *
 * Tracks TWO independent persona dimensions:
 *
 *   1. `persona` (legacy nav slug) — operator | buyer | agency | affiliate
 *      Controls the sidebar shell and routing surfaces. Persisted in
 *      `localStorage.ease_persona`. First-login default: "operator".
 *
 *   2. `personaAxis` + `personaRole` (AG-019 5-axis vocabulary)
 *      `personaAxis` ∈ PERSONA_AXES from @ease/shared (owner|agency|staff|client).
 *      `personaRole` is a free-form string scoped to the tenant's role taxonomy.
 *      Together they key the `usePersonaPack(axis, role)` hook into the
 *      Phase 3b agency route and drive terminology rewriting (`deal` →
 *      "Engagement" for an (agency, staff) pack, etc.).
 *
 * The two dimensions are intentionally separate: `persona` decides which UI
 * shell is mounted, while `personaAxis` decides which copy renders inside
 * that shell. Most callers only need one or the other.
 *
 * Migration from legacy localStorage: a first-load helper folds the legacy
 * `persona` slug into a sensible default `personaAxis` so existing sessions
 * do not see a flicker.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { PERSONA_AXES, type PersonaAxis } from "@ease/shared";

export type Persona = "operator" | "buyer" | "agency" | "affiliate";

const PERSONA_SLUGS: readonly Persona[] = [
  "operator",
  "buyer",
  "agency",
  "affiliate",
] as const;

const PERSONA_STORAGE_KEY = "ease_persona";
const PERSONA_AXIS_STORAGE_KEY = "ease_persona_axis";
const PERSONA_ROLE_STORAGE_KEY = "ease_persona_role";

const DEFAULT_PERSONA: Persona = "operator";
const DEFAULT_PERSONA_AXIS: PersonaAxis = "owner";
const DEFAULT_PERSONA_ROLE = "*";

interface PersonaContextValue {
  /** Legacy nav-shell slug. Drives sidebar visibility. */
  persona: Persona | null;
  setPersona: (next: Persona) => void;
  isPersonaSet: boolean;
  /** AG-019 axis. Drives terminology rewriting via usePersonaPack. */
  personaAxis: PersonaAxis;
  setPersonaAxis: (next: PersonaAxis) => void;
  /** AG-019 role token (free-form, scoped to tenant role taxonomy). */
  personaRole: string;
  setPersonaRole: (next: string) => void;
}

function isPersonaSlug(value: unknown): value is Persona {
  return typeof value === "string" && (PERSONA_SLUGS as readonly string[]).includes(value);
}

function isPersonaAxis(value: unknown): value is PersonaAxis {
  return typeof value === "string" && (PERSONA_AXES as readonly string[]).includes(value);
}

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage write failed — in-memory state still works for this session.
  }
}

function loadPersona(): Persona {
  const stored = readLocalStorage(PERSONA_STORAGE_KEY);
  return isPersonaSlug(stored) ? stored : DEFAULT_PERSONA;
}

/**
 * Maps the legacy nav-shell slug to a starting AG-019 axis so existing
 * sessions land on a reasonable default before the user picks one explicitly.
 *
 *   operator  -> owner   (account owner viewing their own data)
 *   buyer     -> client  (end-customer of the tenant)
 *   agency    -> agency  (agency operator)
 *   affiliate -> staff   (staff-side surfaces)
 */
function personaSlugToAxis(slug: Persona | null): PersonaAxis {
  switch (slug) {
    case "operator":
      return "owner";
    case "buyer":
      return "client";
    case "agency":
      return "agency";
    case "affiliate":
      return "staff";
    default:
      return DEFAULT_PERSONA_AXIS;
  }
}

function loadPersonaAxis(fallbackSlug: Persona | null): PersonaAxis {
  const stored = readLocalStorage(PERSONA_AXIS_STORAGE_KEY);
  if (isPersonaAxis(stored)) return stored;
  return personaSlugToAxis(fallbackSlug);
}

function loadPersonaRole(): string {
  const stored = readLocalStorage(PERSONA_ROLE_STORAGE_KEY);
  // Role tokens must match the route's /^[A-Za-z0-9_*-]+$/ guard.
  if (typeof stored === "string" && /^[A-Za-z0-9_*-]+$/.test(stored)) {
    return stored;
  }
  return DEFAULT_PERSONA_ROLE;
}

const PersonaContext = createContext<PersonaContextValue>({
  persona: null,
  setPersona: () => {},
  isPersonaSet: false,
  personaAxis: DEFAULT_PERSONA_AXIS,
  setPersonaAxis: () => {},
  personaRole: DEFAULT_PERSONA_ROLE,
  setPersonaRole: () => {},
});

export function PersonaProvider({ children }: { children: ReactNode }) {
  const [persona, setPersonaState] = useState<Persona | null>(loadPersona);
  const [personaAxis, setPersonaAxisState] = useState<PersonaAxis>(() =>
    loadPersonaAxis(loadPersona()),
  );
  const [personaRole, setPersonaRoleState] = useState<string>(loadPersonaRole);

  // Keep the AG-019 axis in sync with the legacy nav slug when the user has
  // not yet explicitly chosen an axis. This is a one-way migration: once
  // `personaAxis` is persisted, slug changes do NOT overwrite it.
  useEffect(() => {
    const hasExplicitAxis = readLocalStorage(PERSONA_AXIS_STORAGE_KEY) !== null;
    if (!hasExplicitAxis && persona !== null) {
      setPersonaAxisState(personaSlugToAxis(persona));
    }
  }, [persona]);

  const setPersona = useCallback((next: Persona) => {
    setPersonaState(next);
    writeLocalStorage(PERSONA_STORAGE_KEY, next);
  }, []);

  const setPersonaAxis = useCallback((next: PersonaAxis) => {
    if (!isPersonaAxis(next)) return;
    setPersonaAxisState(next);
    writeLocalStorage(PERSONA_AXIS_STORAGE_KEY, next);
  }, []);

  const setPersonaRole = useCallback((next: string) => {
    if (!/^[A-Za-z0-9_*-]+$/.test(next)) return;
    setPersonaRoleState(next);
    writeLocalStorage(PERSONA_ROLE_STORAGE_KEY, next);
  }, []);

  return (
    <PersonaContext.Provider
      value={{
        persona,
        setPersona,
        isPersonaSet: persona !== null,
        personaAxis,
        setPersonaAxis,
        personaRole,
        setPersonaRole,
      }}
    >
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersona() {
  return useContext(PersonaContext);
}

/**
 * Narrow hook for AG-019 terminology consumers. Returns just the axis +
 * role pair so callers can pass it directly into `usePersonaPack`.
 */
export function usePersonaAxis(): {
  personaAxis: PersonaAxis;
  personaRole: string;
  setPersonaAxis: (next: PersonaAxis) => void;
  setPersonaRole: (next: string) => void;
} {
  const ctx = useContext(PersonaContext);
  return {
    personaAxis: ctx.personaAxis,
    personaRole: ctx.personaRole,
    setPersonaAxis: ctx.setPersonaAxis,
    setPersonaRole: ctx.setPersonaRole,
  };
}

export {
  PERSONA_STORAGE_KEY,
  PERSONA_AXIS_STORAGE_KEY,
  PERSONA_ROLE_STORAGE_KEY,
  DEFAULT_PERSONA_AXIS,
  DEFAULT_PERSONA_ROLE,
  personaSlugToAxis,
};
